import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMediaQuery } from "react-responsive";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import TableWithPagination from "../../../components/TableWithPagination";
import Filter from "../../../components/Filter";
import FilterModal from "../../../components/FilterModal";
import { Tag } from "../../../models/Filter";
import { downloadEstimate, getEstimates } from "../../../services/estimateService";
import { crm } from "../components/crmTheme";

// Extensões que aparecem na pasta de estimativas, para o mime type do download.
const mimeTypes: Record<string, string> = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  zip: "application/zip",
  msg: "application/vnd.ms-outlook",
};

const filterTypes = [
  { label: "Pesquisar por cliente", type: "clientName" },
  { label: "Pesquisar por nome do arquivo", type: "name" },
];

/** Aba "Arquivos": lista de estimativas (arquivos) da pasta compartilhada. */
const FilesTab = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [downloadingPath, setDownloadingPath] = useState<string>();
  const [tags, setTags] = useState<Tag[]>([]);
  const [page, setPage] = useState<any[]>([]);
  const [filteredPage, setFilteredPage] = useState<any[]>([]);
  const [filter, setFilter] = useState<any>();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [initialDate, setInitialDate] = useState<string>();
  const [finalDate, setFinalDate] = useState<string>();
  const [modalOpen, setModalOpen] = useState(false);

  const joinTags = (type: string) =>
    tags
      .filter((t) => t.type === type)
      ?.map((t) => t.value)
      .join("|") || undefined;

  const get = async (
    localPageSize?: number,
    localPageNumber?: number,
    sortField?: string,
    sortDirection?: string
  ) => {
    setLoading(true);

    const response = await getEstimates({
      pageSize: localPageSize ?? pageSize,
      pageNumber: localPageNumber ?? pageIndex,
      sortField,
      sortDirection,
      initialDate,
      finalDate,
      clientName: joinTags("clientName"),
      name: joinTags("name"),
    });

    setPage(response?.data ?? []);
    setFilteredPage(response?.data ?? []);
    setLoading(false);
    return response;
  };

  useEffect(() => {
    get();
  }, [tags]);

  useEffect(() => {
    if (initialDate && finalDate) get();
  }, [initialDate, finalDate]);

  const download = async (estimate: any) => {
    setDownloadingPath(estimate.path);

    const file = await downloadEstimate(estimate.path);

    if (!file) {
      toast({
        title: "Não foi possível baixar a estimativa.",
        description: "O arquivo pode ter sido movido ou renomeado na pasta.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setDownloadingPath(undefined);
      return;
    }

    const extension = estimate.name.split(".").pop()?.toLowerCase() ?? "";
    const mime = mimeTypes[extension] ?? "application/octet-stream";

    const link = document.createElement("a");
    link.href = `data:${mime};base64,${file}`;
    link.download = estimate.name;
    link.click();

    setDownloadingPath(undefined);
  };

  const columns = isMobile
    ? [
        {
          label: "Estimativa",
          data: (e: any) => (
            <Box>
              <Text fontWeight="600" fontSize="sm">
                {e.clientName}
              </Text>
              <Text fontSize="xs" color={crm.textMuted}>
                {e.name}
              </Text>
            </Box>
          ),
        },
        {
          label: "",
          data: (e: any) => (
            <IconButton
              isLoading={downloadingPath === e.path}
              onClick={() => download(e)}
              aria-label="Baixar"
              variant="ghost"
              color={crm.brand}
              _hover={{ bg: crm.brandSoft }}
              icon={<FontAwesomeIcon icon={faDownload} />}
            />
          ),
        },
      ]
    : [
        {
          label: "Cliente",
          index: "clientName",
          sortable: true,
          width: "260px",
          data: (e: any) => (
            <Text fontWeight="600">{e.clientName}</Text>
          ),
        },
        {
          label: "Arquivo",
          index: "name",
          sortable: true,
          data: (e: any) => <Text>{e.name}</Text>,
        },
        {
          label: "Subpasta",
          width: "160px",
          data: (e: any) => (
            <Text color={crm.textMuted}>{e.folder ?? "-"}</Text>
          ),
        },
        {
          label: "Data",
          index: "fileDate",
          sortable: true,
          width: "120px",
          data: (e: any) => (
            <Text>
              {e.fileDate
                ? new Date(e.fileDate).toLocaleDateString("pt-BR")
                : "-"}
            </Text>
          ),
        },
        {
          label: "",
          width: "60px",
          data: (e: any) => (
            <IconButton
              isLoading={downloadingPath === e.path}
              onClick={() => download(e)}
              aria-label="Baixar"
              variant="ghost"
              color={crm.brand}
              _hover={{ bg: crm.brandSoft }}
              icon={<FontAwesomeIcon icon={faDownload} />}
            />
          ),
        },
      ];

  return (
    <Flex alignItems="center" flexDirection="column">
          <Flex
            backgroundColor={crm.surface}
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            borderRadius="12px"
            width="100%"
            paddingBottom="32px"
            mb="24px"
          >
            <FilterModal
              types={filterTypes}
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              {...{ tags, setTags }}
            />
            {isMobile ? (
              <Flex w="100%" justifyContent="center" mt="32px">
                <Button
                  w="80%"
                  backgroundColor="var(--icon-color)"
                  color="white"
                  onClick={() => setModalOpen(true)}
                >
                  Filtrar estimativas
                </Button>
              </Flex>
            ) : (
              <Box pl="32px" pt="32px" borderRadius="8px">
                <Filter
                  extraFilters={[
                    <Box>
                      <Heading size="sm" mb="8px">
                        Início do período
                      </Heading>
                      <Input
                        backgroundColor="white"
                        type="date"
                        width={isMobile ? "80vw" : "16vw"}
                        value={initialDate}
                        onChange={(e) => setInitialDate(e.target.value)}
                      />
                    </Box>,
                    <Box>
                      <Heading size="sm" mb="8px">
                        Fim do período
                      </Heading>
                      <Input
                        backgroundColor="white"
                        type="date"
                        width={isMobile ? "80vw" : "16vw"}
                        value={finalDate}
                        onChange={(e) => setFinalDate(e.target.value)}
                      />
                    </Box>,
                  ]}
                  types={filterTypes}
                  {...{ tags, setTags }}
                />
              </Box>
            )}
          </Flex>

          <Flex
            width="100%"
            height="100%"
            backgroundColor={crm.surface}
            borderRadius="12px"
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            overflow="hidden"
            mb="32px"
            flexDirection="column"
          >
            <Box p={isMobile ? 0 : "32px"} pt="32px">
              <TableWithPagination
                loading={loading}
                columns={columns}
                page={filteredPage}
                setPage={setFilteredPage}
                {...{
                  filter,
                  setFilter,
                  tags,
                  get,
                  pageIndex,
                  setPageIndex,
                  pageSize,
                  setPageSize,
                }}
                emptyMessage="Nenhuma estimativa encontrada"
              />
            </Box>
          </Flex>
    </Flex>
  );
};

export default FilesTab;
